import React, { useCallback, useMemo, useRef, useState } from "react";

import cx from "classnames";
import { AnimatePresence, motion } from "framer-motion";
import { throttle } from "lodash";

import { useMedia, useResizeObserver } from "~/lib/hooks";

import { useIsPublic } from "../utils";
import { PlayButtonSpeedControls, useSpeedToolbarEnabled } from "./PlayButtonSpeedControls";

import styles from "./CenteredControls.module.css";

interface CenteredControlsProps {
	children: React.ReactNode;
	videoRef: React.RefObject<HTMLVideoElement>;
	inClippingMode: boolean;
	collapsed: boolean;
}

export interface CenteredControlsRenderProps {
	centeredControlsVisible: boolean;
}

const NARROW_WIDTH = 400;
const DEFAULT_PLAYBACK_RATE = 1.5;

const CenteredControls: React.FC<CenteredControlsProps> = ({ children, videoRef, inClippingMode, collapsed }) => {
	const speedToolbarEnabled = useSpeedToolbarEnabled();
	const isPublic = useIsPublic();

	const [playbackRateValue, setPlaybackRateValue] = useState(isPublic ? 1 : DEFAULT_PLAYBACK_RATE);

	const setPlaybackRate = useCallback(
		(rate: number) => {
			setPlaybackRateValue(rate);
			if (videoRef.current) {
				videoRef.current.playbackRate = rate;
			}
		},
		[videoRef],
	);

	const playVideo = useCallback(() => {
		if (videoRef.current) {
			videoRef.current.play();
		}
	}, [videoRef]);

	const [isHovered, setIsHovered] = useState(false);

	const clientCanHover = useMedia("(hover: hover)");

	const [isNarrow, setIsNarrow] = useState(false);

	const { ref: containerRef } = useResizeObserver<HTMLDivElement>({
		onResize: useMemo(
			() =>
				throttle(({ width }) => {
					if (!width) return;

					setIsNarrow(width < NARROW_WIDTH);
				}, 200),
			[],
		),
	});
	const playedPreviously = useRef(false);

	if (!speedToolbarEnabled) {
		return typeof children === "function"
			? children({ centeredControlsVisible: false } as CenteredControlsRenderProps)
			: children;
	}

	const duration = videoRef.current?.duration || 0;
	const currentTime = videoRef.current?.currentTime || 0;
	const isPlaying = videoRef.current?.paused === false;

	// We only want to show the controls when the video is first loaded and not playing.
	// Once the user has played the video, even for a second, we should never show this again.
	playedPreviously.current = playedPreviously.current || isPlaying;

	const showControls =
		currentTime === 0 &&
		!inClippingMode &&
		duration > 0 &&
		clientCanHover &&
		!isPlaying &&
		!playedPreviously.current &&
		!collapsed;

	return (
		<div
			className={cx(styles.centeredControlsContainer, { [styles.dimmer]: showControls })}
			onMouseEnter={() => setIsHovered(true)}
			onMouseLeave={() => setIsHovered(false)}
			ref={containerRef}
		>
			<AnimatePresence initial={false}>
				{showControls && (
					<motion.div
						className={styles.controlsOverlay}
						initial={{ opacity: 0 }}
						animate={{ opacity: 1 }}
						exit={{ opacity: 0 }}
						transition={{ duration: 0.25 }}
						key="speed-controls"
					>
						<PlayButtonSpeedControls
							setPlaybackRate={setPlaybackRate}
							playbackRate={playbackRateValue}
							playVideo={playVideo}
							duration={duration}
							isExpanded={isHovered}
							compact={isNarrow}
						/>
					</motion.div>
				)}
			</AnimatePresence>
			{typeof children === "function"
				? children({ centeredControlsVisible: showControls } as CenteredControlsRenderProps)
				: children}
		</div>
	);
};

export default CenteredControls;
